/**
 * Google Places Autocomplete using Web Components
 * Based on the Google Maps JS API samples - places-autocomplete-addressform
 * Uses the gmp-place-autocomplete element for address input
 */

import { useEffect, useRef, useCallback } from 'react';

export interface PlaceResult {
  name: string;
  address: string;
  lat: number | null;
  lng: number | null;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  addressComponents?: google.maps.places.AddressComponent[];
}

interface PlaceAutocompleteTextFieldProps {
  onPlaceSelect?: (place: PlaceResult) => void;
  placeholder?: string;
  label?: string;
  style?: React.CSSProperties;
}

export function PlaceAutocompleteTextField({
  onPlaceSelect,
  placeholder = 'Search for an address',
  label,
  style,
}: PlaceAutocompleteTextFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<any>(null);
  const initializedRef = useRef(false);
  const callbackRef = useRef(onPlaceSelect);
  
  // Keep callback ref up to date
  callbackRef.current = onPlaceSelect;

  const parseAddressComponents = useCallback((addressComponents: google.maps.places.AddressComponent[]) => {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let postalCodeSuffix = '';
    let country = '';

    for (const component of addressComponents) {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.longText || '';
      }
      if (types.includes('route')) {
        route = component.shortText || '';
      }
      if (types.includes('locality')) {
        city = component.longText || '';
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.shortText || '';
      }
      if (types.includes('postal_code')) {
        postalCode = component.longText || '';
      }
      if (types.includes('postal_code_suffix')) {
        postalCodeSuffix = component.longText || '';
      }
      if (types.includes('country')) {
        country = component.longText || '';
      }
    }

    const streetAddress = streetNumber ? `${streetNumber} ${route}` : route;
    const fullPostalCode = postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode;

    return { streetAddress, city, state, postalCode: fullPostalCode, country };
  }, []);

  useEffect(() => {
    if (initializedRef.current || !containerRef.current) return;
    
    const init = async () => {
      try {
        // Import the places library
        await google.maps.importLibrary('places');

        // Create the gmp-place-autocomplete element
        const autocomplete = document.createElement('gmp-place-autocomplete') as any;
        autocomplete.setAttribute('placeholder', placeholder);
        
        // Style the autocomplete element
        autocomplete.style.cssText = `
          width: 100%;
          height: 48px;
          --gmp-place-autocomplete-input-font-size: 16px;
          --gmp-place-autocomplete-input-padding: 12px 16px;
          --gmp-place-autocomplete-input-border-radius: 8px;
          --gmp-place-autocomplete-input-border: 1px solid #ccc;
        `;

        // Handle place selection
        autocomplete.addEventListener('gmp-select', async (event: any) => {
          const placePrediction = event.placePrediction;
          if (!placePrediction) return;

          try {
            // Convert prediction to Place and fetch full details
            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
            });

            // Parse address components
            const parsed = place.addressComponents 
              ? parseAddressComponents(place.addressComponents)
              : { streetAddress: '', city: '', state: '', postalCode: '', country: '' };

            const result: PlaceResult = {
              name: place.displayName || '',
              address: place.formattedAddress || '',
              lat: place.location?.lat() ?? null,
              lng: place.location?.lng() ?? null,
              ...parsed,
              addressComponents: place.addressComponents,
            };

            callbackRef.current?.(result);
          } catch (error) {
            console.error('Error fetching place details:', error);
          }
        });

        containerRef.current?.appendChild(autocomplete);
        autocompleteRef.current = autocomplete;
        initializedRef.current = true;

        // Removed auto-focus to prevent input from being focused on mount
      } catch (error) {
        console.error('Error initializing Places Autocomplete:', error);
      }
    };

    // Check if Google Maps is loaded
    if (typeof google !== 'undefined' && google.maps) {
      init();
    } else {
      console.error('Google Maps JavaScript API not loaded');
    }
  }, [placeholder, parseAddressComponents]);

  return (
    <div style={{ width: '100%', ...style }}>
      {label && (
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontWeight: 500,
          color: '#333',
          fontSize: '14px'
        }}>
          {label}
        </label>
      )}
      <div 
        ref={containerRef} 
        style={{ width: '100%' }}
        data-testid="place-autocomplete-input"
      />
    </div>
  );
}

export default PlaceAutocompleteTextField;
